import React, { Component, ReactNode } from 'react';
import { connect } from 'react-redux';
import { Redirect, RouteComponentProps, withRouter } from 'react-router-dom';
import qs from 'qs';

import { RootState } from '../../slices/rootReducer';
import { refreshToken } from '../../slices/auth/refreshToken.thunks';
import { fetchWhoAmI } from '../../slices/auth/login.thunks';
import { selectors as authSelectors } from '../../slices/auth';
import SessionModal from './sessionModal';
import Loader from '../common/loader';
import { CORE_APP_URL, getAuthorizeUrl } from '../../config';
import { IUser } from '../../slices/users/user';
import { isIncrowdClient } from '../../utils/clients';
import { getQueryParam } from '../../utils/queryParameters';
import { getRedirectionPath } from './utils/getRedirectionPath';
import { getUrlToRedirect, isNoneCoreUrl } from './utils/getUrlToRedirect';
import { UserTypes } from '../common/domain/userTypes';
import { Features, isFeatureEnabled } from '../../featureToggles/features';

interface Props extends RouteComponentProps {
  children: React.ReactElement;
  redirectByPath: Record<string, string>;
}

interface StateProps {
  isLoggedIn: boolean;
  is2faEnabled: boolean;
  uniqueSession: boolean;
  loading: boolean;
  authError: string | null;
  userId: string | null;
  user: IUser | null;
  token: string;
  redirectUrl: string;
  isRefreshingToken: boolean;
  webClientId: string | null;
}

interface DispatchProps {
  refreshToken: () => void;
  fetchWhoAmI: () => void;
}

interface ILoggedInContainerState {
  internalLoading: boolean;
}

type LoggedInContainerProps = Props & StateProps & DispatchProps;

const SX_ICODE = 'sx_icode';

class LoggedInContainer extends Component<LoggedInContainerProps, ILoggedInContainerState> {
  constructor(props: LoggedInContainerProps) {
    super(props);
    this.state = {
      internalLoading: true && !props.isLoggedIn && !isIncrowdClient() && !isFeatureEnabled(Features.WorkosSSO)
    };
  }

  componentDidMount(): void {
    const { isLoggedIn, refreshToken } = this.props;
    if (isLoggedIn) {
      if (!isFeatureEnabled(Features.ShareableDashboards)) {
        this.props.fetchWhoAmI();
      }
      return;
    }
    refreshToken();
  }

  public componentDidUpdate(prevProps: LoggedInContainerProps) {
    const { userId, fetchWhoAmI, isLoggedIn, history, loading } = this.props;
    const path = history.location.pathname;
    if (!prevProps.loading && loading) {
      this.setState({
        internalLoading: false
      });
    }
    if (!prevProps.isLoggedIn && isLoggedIn && path === '/login') {
      history.replace('/');
      fetchWhoAmI();
    }
    if (this.props.isLoggedIn && userId && prevProps.userId !== userId) {
      fetchWhoAmI();
    }

    // This is a temporal fix since we need to actually have a logged in user
    // to start the upgrade process, now the endpoint is not sending user data
    // instead it returns an error and that is the reason for internalLoading
    // never change to false.
    if (this.state.internalLoading && isLoggedIn && history.location.pathname === '/expired-account') {
      this.setState({
        internalLoading: false
      });
    }
  }

  private performRedirectIfIncrowd() {
    const { user, token, webClientId } = this.props;

    const result = (() => {
      const encodedState = getQueryParam('state'); //state comes when redirecting from cognito
      const returnValue: {
        host?: string;
        pathname?: string;
        sx_icode?: string;
        rest: { [id: string]: string };
      } = {
        host: '',
        pathname: '',
        rest: {}
      };
      if (encodedState) {
        const stateStr = Buffer.from(encodedState, 'base64').toString();
        const parsedState = JSON.parse(stateStr) || {};
        const { redirectUrl, sx_icode, ...rest } = parsedState;
        if (redirectUrl) {
          const redirectURLObject = new URL(redirectUrl);
          returnValue.host = redirectURLObject.host;
          returnValue.pathname = redirectURLObject.pathname;
        }
        if (parsedState.hasOwnProperty(SX_ICODE)) { // keep property even if there is no value
          returnValue.sx_icode = sx_icode;
        }
        returnValue.rest = rest || {};
      }
      return returnValue;
    })();
    const { pathname = '', sx_icode, rest, host = '' } = result;
    if (user?.userType) {
      const redirectUrl = CORE_APP_URL.concat(pathname);
      let redirectUrlWithQParams = `${redirectUrl}?sx_client_id=${webClientId}&userId=${user?.id}&token=${token}`;
      redirectUrlWithQParams = result.hasOwnProperty(SX_ICODE) ? redirectUrlWithQParams.concat(`&${SX_ICODE}=${sx_icode}`) : redirectUrlWithQParams;
      const restParams: string[] = [];
      Object.keys(rest).forEach(key => {
        const value = rest[key];
        restParams.push(value ? `${key}=${value}` : key);
      });
      redirectUrlWithQParams = restParams.length > 0 ? redirectUrlWithQParams.concat(`&${restParams.join('&')}`) : redirectUrlWithQParams;
      if( [UserTypes.SxAdmin, UserTypes.MainAdmin].includes(user.userType) && !host ) {
        //intended
      } else {
        window.location.assign(redirectUrlWithQParams);
      }
    }
  }

  render(): ReactNode {
    const { internalLoading } = this.state;
    const { redirectByPath = {} } = this.props;

    if (internalLoading) {
      return null;
    }

    const { loading, isLoggedIn, uniqueSession, user, is2faEnabled, token, isRefreshingToken, webClientId, location } = this.props;
    const { redirectUrl = '' } = qs.parse(location.search, { ignoreQueryPrefix: true });

    if (loading || (!isLoggedIn && isRefreshingToken) || (!user && isIncrowdClient() && loading && !isLoggedIn)) {
      return <Loader />;
    }

    if (!isLoggedIn) {
      const currentPath = window.location.pathname;
      if (isIncrowdClient()) {
        if (!currentPath.includes('/idpresponse') && !isRefreshingToken ) {
          window.location.assign(getAuthorizeUrl(webClientId));
        }
        return null;
      }
      return <Redirect to={getRedirectionPath(redirectByPath, currentPath)} />;
    }

    if (isIncrowdClient() && !window.location.search && window.location.pathname === '/idpresponse') {
      return <Redirect to={'/accounts'} />;
    }

    if (is2faEnabled) {
      //TODO: please check if explicit redirect must be made just after the login payload requires 2fa
      return <Redirect to="/twoFactorAuth" />;
    }

    if (isIncrowdClient()) {
      this.performRedirectIfIncrowd();
      if (user?.userType === UserTypes.User || user?.userType === UserTypes.AdminAssistant) {
        return null;
      }
    }

    const sessionConflictModal = uniqueSession ? null : <SessionModal />;
    if (typeof redirectUrl === 'string' && user) {
      const currentPath = window.location.pathname;
      const validatedUrl = getUrlToRedirect(user, redirectUrl, currentPath);
      const isOnCurrentPath = validatedUrl.pathname === currentPath;
      if (isNoneCoreUrl(validatedUrl)) {
        if (!isOnCurrentPath) {
          return <Redirect to={`${validatedUrl.pathname}${validatedUrl.search}`} />;
        }
      } else {
        validatedUrl.searchParams.append('token', token);
        validatedUrl.searchParams.append('userId', user.id);
        window.location.assign(validatedUrl.toString());
        return null;
      }
    }
    return <>
      {sessionConflictModal}
      {this.props.children}
    </>;
  }
}

const mapStateToProps = (state: RootState): StateProps => ({
  uniqueSession: authSelectors.getUniqueSession(state),
  redirectUrl: authSelectors.getRedirectUrl(state),
  loading: state.auth.loading,
  isLoggedIn: state.auth.isLoggedIn,
  is2faEnabled: authSelectors.is2faEnabled(state),
  authError: state.auth.error,
  userId: authSelectors.getLoggedInUserId(state),
  user: state.auth.user,
  token: state.auth.token,
  isRefreshingToken: authSelectors.getLoadingAuthState(state),
  webClientId: authSelectors.getWebClientId(state)
});

const ConnectedLoggedInContainer = connect(mapStateToProps, {
  refreshToken,
  fetchWhoAmI
})(LoggedInContainer);

export default withRouter(ConnectedLoggedInContainer);
